import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CaseType } from 'src/app/shared/enums/case-type.enum';
import { VinLoadOption } from 'src/app/shared/enums/vin-load-option.enum';
import { CaseSchedulingType } from 'src/app/shared/models/case-creation/case-scheduling-type.model';
import { NeededActionsInfo, SVDSInfo, VehicleModel } from 'src/app/shared/models/case-creation/vehicle.model';
import { sortVinsByDisplayedOrder } from 'src/app/views/case-steps/utils/grid-selection.utils';

@Injectable()
export class CaseCreationStore {
  private readonly router = inject(Router);

  readonly dbClickedVehicle = signal<VehicleModel | undefined>(undefined);

  readonly currentStep = signal<number>(1);
  readonly vinSelectionOption = signal<VinLoadOption>(VinLoadOption.FSS);
  readonly initialVins = signal<Map<VinLoadOption, Set<string>>>(
    new Map([
      [VinLoadOption.VINS_LIST, new Set([])],
      [VinLoadOption.VINS_FILE, new Set([])],
    ])
  );
  readonly selectedVins = signal<Map<number, string[]>>(
    new Map([
      [2, []],
      [3, []],
    ])
  );
  readonly svdsInfo = signal<SVDSInfo[] | undefined>(undefined);
  readonly neededActionsInfo = signal<NeededActionsInfo[] | undefined>(undefined);
  readonly consents = signal<Set<string>>(new Set([]));
  readonly caseType = signal<CaseType>(CaseType.PDI);
  readonly bulkID = signal<string>('');
  readonly vehiclesListName = signal<string>('');
  readonly wifiId = signal<string | undefined>(undefined);

  readonly scheduleType = signal<CaseSchedulingType>(CaseSchedulingType.NOW);
  readonly schedulingDate = signal<Date>(new Date());

  readonly stepSelectedVins = computed(() => this.selectedVins().get(this.currentStep()) ?? []);

  readonly caseLanguage = signal<string>('en');

  readonly validationCampaignVins = signal<Map<string, boolean>>(new Map());

  async cleanStore(): Promise<void> {
    await this.router.navigate(['/dashboard/open']);
    this.currentStep.set(1);
    this.vinSelectionOption.set(VinLoadOption.FSS);
    this.initialVins.set(
      new Map([
        [VinLoadOption.VINS_LIST, new Set([])],
        [VinLoadOption.VINS_FILE, new Set([])],
      ])
    );
    this.selectedVins.set(
      new Map([
        [2, []],
        [3, []],
      ])
    );
    this.svdsInfo.set(undefined);
    this.neededActionsInfo.set(undefined);
    this.consents.set(new Set([]));
    this.caseType.set(CaseType.PDI);
    this.wifiId.set(undefined);
    this.bulkID.set('');
    this.vehiclesListName.set('');
    this.dbClickedVehicle.set(undefined);
    this.validationCampaignVins.set(new Map());
    this.resetSchedulingInformation();
  }

  getPreviousStepSelectedVins(): string[] {
    return this.selectedVins().get(this.currentStep() - 1) ?? [];
  }

  getInitialSelectedVins(): Set<string> {
    return this.initialVins().get(this.vinSelectionOption()) ?? new Set<string>();
  }

  setInitialSelectedVins(vins: string[]): void {
    this.initialVins.update((state) => new Map(state.set(this.vinSelectionOption(), new Set<string>(vins))));
  }

  setSvdsInformation(svdsInfo: SVDSInfo[]): void {
    this.svdsInfo.set(svdsInfo);
  }

  resetSvdsInformation(): void {
    this.svdsInfo.set(undefined);
  }

  getVinSvdsInformation(vin: string): SVDSInfo {
    return this.svdsInfo()!.find((vehicle) => vehicle.vin === vin)!;
  }

  setSelectedVins(selectedVins: string[], displayedVins?: string[]): void {
    const orderedVins = displayedVins
      ? sortVinsByDisplayedOrder(selectedVins, displayedVins) : [...new Set<string>(selectedVins)];
    this.selectedVins.update((state) => new Map(state.set(this.currentStep(), orderedVins)));
  }

  resetSelectedVins(): void {
    this.selectedVins.update((state) => new Map(state.set(this.currentStep(), [])));
  }

  resetBulkId(): void {
    this.bulkID.set('');
  }

  setSelectedWifiId(wifiId: string): void {
    this.wifiId.set(wifiId);
  }

  resetSelectedWifiId(): void {
    this.wifiId.set(undefined);
  }

  setSchedulingDate(date: Date): void {
    this.schedulingDate.set(date);
  }

  getSchedulingDateIfNeeded(): Date | undefined {
    return this.scheduleType() === CaseSchedulingType.LATER ? this.schedulingDate() : undefined;
  }

  resetSchedulingInformation(): void {
    this.scheduleType.set(CaseSchedulingType.NOW);
    this.schedulingDate.set(new Date());
  }

  addConsent(vin: string): void {
    this.consents.update((state) => new Set([vin, ...state]));
  }

  vinHasConsent(vin: string): boolean {
    return this.consents().has(vin);
  }

  cleanConsents(): void {
    this.consents.set(new Set([]));
  }

  selectNextStepperItem(): void {
    if (this.currentStep() !== 4) {
      const currentStep = this.currentStep();
      const nextStep = currentStep + 1;

      if (currentStep === 2) {
        const previousStepSelection = this.selectedVins().get(currentStep) ?? [];
        this.selectedVins.update((state) => new Map(state.set(nextStep, [...previousStepSelection])));
      }

      this.currentStep.set(nextStep);
    }
  }

  selectPreviousStepperItem(): void {
    if (this.currentStep() !== 1) {
      this.currentStep.update((state) => state - 1);
    }
  }

  setNeededActionsInformation(neededActionsInfo: NeededActionsInfo[]): void {
    this.neededActionsInfo.set(neededActionsInfo);
  }

  resetNeededActionsInformation(): void {
    this.neededActionsInfo.set(undefined);
  }

  getVinNeededActionsInformation(vin: string): NeededActionsInfo {
    return this.neededActionsInfo()!.find((neededAction) => neededAction.vin === vin)!;
  }

  setDbClickedVehicle(vehicle: VehicleModel): void {
    this.resetDbClickedVehicle();
    this.dbClickedVehicle.set(vehicle);
  }

  resetDbClickedVehicle(): void {
    this.dbClickedVehicle.set(undefined);
  }

  setVehiclesListName(listName: string): void {
    this.vehiclesListName.set(listName);
  }

  setCaseLanguage(language: string): void {
    this.caseLanguage.set(language);
  }

  initializeValidationCampaignVins(vins: string[], defaultEnabled: boolean = true): void {
    const currentMap = this.validationCampaignVins();
    const newMap = new Map<string, boolean>();
    vins.forEach((vin) => newMap.set(vin, currentMap.get(vin) ?? defaultEnabled));
    this.validationCampaignVins.set(newMap);
  }

  setVinValidationCampaign(vin: string, validationCampaign: boolean): void {
    this.validationCampaignVins.update((state) => new Map(state.set(vin, validationCampaign)));
  }

  getVinValidationCampaign(vin: string): boolean {
    return this.validationCampaignVins().get(vin) ?? false;
  }
}
